import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowLeft } from 'lucide-react';

const TermsOfService = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
      <header className="glass sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <Button
            onClick={() => navigate(-1)}
            variant="ghost"
            className="text-white hover:bg-white/20"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-12">
        <Card className="glass border-white/20">
          <CardHeader>
            <CardTitle className="text-3xl text-white" style={{ fontFamily: 'Space Grotesk' }}>
              Terms of Service
            </CardTitle>
            <p className="text-white/80 text-sm mt-2">Last Updated: October 24, 2025</p>
          </CardHeader>
          <CardContent className="text-white space-y-6">
            <section>
              <h2 className="text-xl font-semibold mb-3">1. Acceptance of Terms</h2>
              <p className="text-white/90 leading-relaxed">
                By accessing and using the Chem-Steel 316L Compatibility Checker ("Service"), you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to these terms, please do not use this Service.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">2. Description of Service</h2>
              <p className="text-white/90 leading-relaxed">
                The Service provides AI-powered chemical compatibility analysis for stainless steel 316L materials. The Service includes chemical analysis reports, transport code information, and safety recommendations based on available data and AI analysis.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">3. Informational Purposes Only</h2>
              <p className="text-white/90 leading-relaxed mb-3">
                <strong>IMPORTANT DISCLAIMER:</strong> The information provided by this Service is for informational and educational purposes only. It should NOT be used as the sole basis for critical safety decisions.
              </p>
              <ul className="list-disc list-inside space-y-2 text-white/90">
                <li>Always consult official Safety Data Sheets (SDS) from chemical manufacturers</li>
                <li>Verify all compatibility information with qualified chemical engineers</li>
                <li>Follow all applicable safety regulations and industry standards</li>
                <li>This Service does not replace professional chemical safety consultation</li>
                <li>Users are solely responsible for their use of the information provided</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">4. No Warranty</h2>
              <p className="text-white/90 leading-relaxed">
                THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED. We do not warrant that the information provided is accurate, complete, reliable, current, or error-free. We disclaim all liability for any damages arising from reliance on the information provided by this Service.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">5. User Accounts and Subscriptions</h2>
              <p className="text-white/90 leading-relaxed mb-3">
                Users may create accounts to access the Service. Subscription terms:
              </p>
              <ul className="list-disc list-inside space-y-2 text-white/90">
                <li>New users receive a 10-day free trial</li>
                <li>After trial, continued access requires an active subscription</li>
                <li>Subscriptions are billed monthly at the current rate</li>
                <li>Users may cancel subscriptions at any time</li>
                <li>No refunds for partial subscription periods</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">6. User Responsibilities</h2>
              <p className="text-white/90 leading-relaxed">
                Users agree to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-white/90">
                <li>Provide accurate account information</li>
                <li>Maintain the security of their account credentials</li>
                <li>Use the Service only for lawful purposes</li>
                <li>Not attempt to reverse engineer or compromise the Service</li>
                <li>Not share account access with unauthorized users</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">7. Limitation of Liability</h2>
              <p className="text-white/90 leading-relaxed">
                IN NO EVENT SHALL WE BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOSS OF PROFITS, DATA, OR OTHER INTANGIBLE LOSSES, RESULTING FROM YOUR USE OF THE SERVICE, EVEN IF WE HAVE BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">8. Intellectual Property</h2>
              <p className="text-white/90 leading-relaxed">
                All content, features, and functionality of the Service, including but not limited to text, graphics, logos, and software, are owned by us and protected by international copyright, trademark, and other intellectual property laws.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">9. Modifications to Service</h2>
              <p className="text-white/90 leading-relaxed">
                We reserve the right to modify, suspend, or discontinue the Service at any time without notice. We may also update these Terms of Service periodically. Continued use of the Service after changes constitutes acceptance of the modified terms.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">10. Governing Law</h2>
              <p className="text-white/90 leading-relaxed">
                These Terms shall be governed by and construed in accordance with applicable laws, without regard to conflict of law provisions.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">11. Contact Information</h2>
              <p className="text-white/90 leading-relaxed">
                For questions about these Terms of Service, please contact us at:{' '}
                <a href="mailto:info@corrosioncheck.com" className="text-blue-300 hover:text-blue-200 underline">
                  info@corrosioncheck.com
                </a>
              </p>
            </section>

            <section className="bg-yellow-500/20 border border-yellow-500/40 rounded-lg p-4 mt-6">
              <p className="text-white text-sm">
                <strong>SAFETY NOTICE:</strong> This Service provides informational analysis only. Always verify compatibility information with official sources, consult qualified professionals, and follow all applicable safety regulations before handling chemicals or making material selection decisions.
              </p>
            </section>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default TermsOfService;
